import * as Sentry from '@sentry/react';
import { supabase } from './supabase';
import { createContextLogger } from './logger';
import { QueuedOperation } from '../types/network';
import {
  migrateLegacyStorageKey,
  namespacedStorageKey,
  readSessionScopeIdFromStorage,
} from './tenantStorage';
import { isLogoutInProgress } from './auth';

const log = createContextLogger('ApiClient');

interface RetryConfig {
  maxRetries: number;
  baseDelay: number;
  maxDelay: number;
}

const DEFAULT_RETRY_CONFIG: RetryConfig = {
  maxRetries: 3,
  baseDelay: 1000,
  maxDelay: 10000,
};

const LEGACY_OFFLINE_QUEUE_KEY = 'offline_queue';

function offlineQueueStorageKey(): string {
  const scopeId = readSessionScopeIdFromStorage();
  const key = namespacedStorageKey(LEGACY_OFFLINE_QUEUE_KEY, scopeId);
  if (scopeId) {
    migrateLegacyStorageKey(LEGACY_OFFLINE_QUEUE_KEY, key);
  }
  return key;
}

function sleep(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms));
}

function getExponentialDelay(attempt: number, config: RetryConfig): number {
  const delay = config.baseDelay * Math.pow(2, attempt);
  return Math.min(delay, config.maxDelay);
}

function isNetworkError(error: unknown): boolean {
  if (error instanceof Error) {
    const message = error.message.toLowerCase();
    return (
      message.includes('network') ||
      message.includes('fetch') ||
      message.includes('timeout') ||
      message.includes('aborted') ||
      message.includes('failed to fetch')
    );
  }
  return false;
}

function isRetryableError(error: unknown): boolean {
  if (isNetworkError(error)) return true;

  if (error && typeof error === 'object' && 'code' in error) {
    const code = (error as { code: string }).code;
    return ['PGRST301', 'PGRST504', '503', '504', '502'].includes(code);
  }

  return false;
}

export async function withRetry<T>(
  operation: () => Promise<T>,
  operationName: string,
  config: Partial<RetryConfig> = {}
): Promise<T> {
  const retryConfig = { ...DEFAULT_RETRY_CONFIG, ...config };
  let lastError: unknown;

  for (let attempt = 0; attempt <= retryConfig.maxRetries; attempt++) {
    try {
      if (isLogoutInProgress()) {
        throw new Error(`${operationName} aborted: logout in progress`);
      }

      if (attempt > 0) {
        const delay = getExponentialDelay(attempt - 1, retryConfig);
        log.debug(`Retrying ${operationName}`, { attempt, delay });
        await sleep(delay);
      }

      return await operation();
    } catch (error) {
      lastError = error;

      if (!isRetryableError(error)) {
        log.error(`${operationName} failed (non-retryable)`, { error: String(error) });
        throw error;
      }

      if (attempt === retryConfig.maxRetries) {
        log.error(`${operationName} failed after ${retryConfig.maxRetries} retries`, { error: String(error) });
        Sentry.captureException(error, { tags: { operation: operationName, retries: retryConfig.maxRetries } });
        throw error;
      }

      log.warn(`${operationName} failed, will retry`, { attempt: attempt + 1, error: String(error) });
    }
  }

  throw lastError;
}

function getOfflineQueue(): QueuedOperation[] {
  try {
    const stored = localStorage.getItem(offlineQueueStorageKey());
    return stored ? JSON.parse(stored) : [];
  } catch {
    return [];
  }
}

function saveOfflineQueue(queue: QueuedOperation[]): void {
  try {
    localStorage.setItem(offlineQueueStorageKey(), JSON.stringify(queue));
    window.dispatchEvent(new Event('storage'));
  } catch {
    log.error('Failed to save offline queue');
  }
}

export function queueOfflineOperation(operation: Omit<QueuedOperation, 'id' | 'timestamp' | 'retryCount'>): void {
  const queue = getOfflineQueue();
  const newOp: QueuedOperation = {
    ...operation,
    id: `op_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`,
    timestamp: new Date(),
    retryCount: 0,
  };
  queue.push(newOp);
  saveOfflineQueue(queue);
  log.info('Operation queued for offline sync', { type: operation.type, table: operation.table });
}

export function getPendingOperationsCount(): number {
  return getOfflineQueue().length;
}

export function clearOfflineQueue(): void {
  const scopeId = readSessionScopeIdFromStorage();
  if (scopeId) {
    localStorage.removeItem(namespacedStorageKey(LEGACY_OFFLINE_QUEUE_KEY, scopeId));
  }
  localStorage.removeItem(LEGACY_OFFLINE_QUEUE_KEY);
  window.dispatchEvent(new Event('storage'));
}

export async function processPendingOperations(): Promise<{ success: number; failed: number }> {
  const queue = getOfflineQueue();
  if (queue.length === 0) return { success: 0, failed: 0 };

  log.info('Processing offline queue', { count: queue.length });

  let success = 0;
  let failed = 0;
  const remainingOps: QueuedOperation[] = [];

  for (const op of queue) {
    try {
      switch (op.type) {
        case 'insert':
          await supabase.from(op.table).insert(op.payload);
          break;
        case 'update':
          if (op.payload.id) {
            const { id, ...data } = op.payload;
            await supabase.from(op.table).update(data).eq('id', id);
          }
          break;
        case 'delete':
          if (op.payload.id) {
            await supabase.from(op.table).delete().eq('id', op.payload.id as string);
          }
          break;
        case 'rpc':
          if (op.payload.functionName) {
            await supabase.rpc(op.payload.functionName as string, op.payload.args as Record<string, unknown>);
          }
          break;
      }
      success++;
      log.debug('Offline operation processed', { type: op.type, table: op.table });
    } catch (error) {
      op.retryCount++;
      if (op.retryCount < op.maxRetries) {
        remainingOps.push(op);
      } else {
        failed++;
        log.error('Offline operation permanently failed', { type: op.type, table: op.table, error: String(error) });
        Sentry.captureException(error, { tags: { offlineOp: op.type, table: op.table } });
      }
    }
  }

  saveOfflineQueue(remainingOps);
  log.info('Offline queue processing complete', { success, failed, remaining: remainingOps.length });

  return { success, failed };
}

interface ApiClientConfig {
  retryConfig?: Partial<RetryConfig>;
  queueOnOffline?: boolean;
}

export function createApiClient(config: ApiClientConfig = {}) {
  const { retryConfig = {}, queueOnOffline = true } = config;

  return {
    async select<T>(
      table: string,
      query: { columns?: string; filters?: Record<string, unknown> } = {}
    ): Promise<{ data: T[] | null; error: Error | null }> {
      return withRetry(async () => {
        let builder = supabase.from(table).select(query.columns || '*');

        if (query.filters) {
          for (const [key, value] of Object.entries(query.filters)) {
            builder = builder.eq(key, value);
          }
        }

        const { data, error } = await builder;
        if (error) throw error;
        return { data: data as T[], error: null };
      }, `SELECT from ${table}`, retryConfig).catch(error => {
        return { data: null, error: error as Error };
      });
    },

    async insert<T>(
      table: string,
      payload: Record<string, unknown>
    ): Promise<{ data: T | null; error: Error | null }> {
      return withRetry(async () => {
        const { data, error } = await supabase.from(table).insert(payload).select().single();
        if (error) throw error;
        return { data: data as T, error: null };
      }, `INSERT into ${table}`, retryConfig).catch(error => {
        if (queueOnOffline && isNetworkError(error)) {
          queueOfflineOperation({ type: 'insert', table, payload, maxRetries: 3, priority: 'normal' });
          return { data: null, error: new Error('Operation queued for offline sync') };
        }
        return { data: null, error: error as Error };
      });
    },

    async update<T>(
      table: string,
      id: string,
      payload: Record<string, unknown>
    ): Promise<{ data: T | null; error: Error | null }> {
      return withRetry(async () => {
        const { data, error } = await supabase.from(table).update(payload).eq('id', id).select().single();
        if (error) throw error;
        return { data: data as T, error: null };
      }, `UPDATE ${table}`, retryConfig).catch(error => {
        if (queueOnOffline && isNetworkError(error)) {
          queueOfflineOperation({ type: 'update', table, payload: { id, ...payload }, maxRetries: 3, priority: 'normal' });
          return { data: null, error: new Error('Operation queued for offline sync') };
        }
        return { data: null, error: error as Error };
      });
    },

    async delete(
      table: string,
      id: string
    ): Promise<{ error: Error | null }> {
      return withRetry(async () => {
        const { error } = await supabase.from(table).delete().eq('id', id);
        if (error) throw error;
        return { error: null };
      }, `DELETE from ${table}`, retryConfig).catch(error => {
        if (queueOnOffline && isNetworkError(error)) {
          queueOfflineOperation({ type: 'delete', table, payload: { id }, maxRetries: 3, priority: 'normal' });
          return { error: new Error('Operation queued for offline sync') };
        }
        return { error: error as Error };
      });
    },
  };
}

export const apiClient = createApiClient();
